import type React from 'react';
import { useCallback, useRef } from 'react';

import { EntryStatusEnum, EventTypeEnum, ParticipantTypeEnum } from '@clubspark-node/clubspark-tennis-types';
import Grid from '@mui/material/Grid';
import type { Row } from '@tanstack/react-table';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { drawDefinitionConstants } from 'tods-competition-factory';

import EditIcon from 'assets/icons/edit.svg?react';
import MoveIcon from 'assets/icons/icn _ 32px _ move.svg?react';
import AddIcon from 'assets/icons/icn-glyph-24-add.svg?react';
import WithdrawIcon from 'assets/icons/icn-glyph-24-remove.svg?react';
import { eventAtomFamily, mainDrawGeneratedAtomFamily } from 'atoms/events';
import ToolsGrid, { ToolButton, ToolText } from 'components/tables/common/ToolsGrid';
import { useStyles } from 'components/tables/competitors/alternates/styles';
import useWithdrawDialog from 'containers/dialogs/WithdrawDialog';
import useMutateTournament from 'hooks/tournament/useMutateTournament';
import type { ParticipantInterface } from 'typedefs/store/tmxTypes';

const { MAIN } = drawDefinitionConstants;
const { TEAM, DOUBLES } = EventTypeEnum;

interface QualifyingBulkActionsProps<T extends Record<string, any>> {
  selectedFlatRows: Row<T>[];
  onClickSetPlayerStatus?: (participantId: string[], resetRowSelection: () => void) => void;
  onResetRowSelection: () => void;
}

const QualifyingBulkActions: React.FC<React.PropsWithChildren<QualifyingBulkActionsProps<ParticipantInterface>>> = ({
  selectedFlatRows,
  onClickSetPlayerStatus,
  onResetRowSelection,
}) => {
  const { classes } = useStyles();
  const { eventId } = useParams<{ eventId: string }>();
  const event = useAtomValue(eventAtomFamily(eventId));
  const isMainDrawGenerated = useAtomValue(mainDrawGeneratedAtomFamily(eventId));

  const { t } = useTranslation();
  const showWithdrawDecisionDialog = useWithdrawDialog();

  const [moveTo] = useMutateTournament('moveTo');
  const [withdrawParticipantFromEvent] = useMutateTournament('withdrawParticipantFromEvent');

  const isTeam = event?.eventType === TEAM;
  const isDoubles = event?.eventType === DOUBLES;
  const isTeamTable =
    isTeam && selectedFlatRows?.some((entry) => entry.original.participantType === ParticipantTypeEnum.TEAM);

  const selected = useRef<ParticipantInterface[]>([]);
  selected.current = selectedFlatRows?.map((row) => row.original);

  const handleMoveToSelected = useCallback(() => {
    moveTo(
      selected.current.map((p) => p?.participantId),
      event,
      false,
      MAIN,
    );
    onResetRowSelection();
  }, [moveTo, event, onResetRowSelection]);

  const handleWithdrawal = useCallback(
    async (moveTo?: 'ALTERNATE' | 'WITHDRAWN' | 'UNGROUPED') => {
      const participantIds = selected.current?.map((p) => p.participantId);

      if (moveTo === EntryStatusEnum.WITHDRAWN) {
        const withdrawDecision = await showWithdrawDecisionDialog(false, false, isTeam);
        if (withdrawDecision) {
          await withdrawParticipantFromEvent(participantIds, EntryStatusEnum.WITHDRAWN, event.eventId);
          onResetRowSelection();
        }
      } else {
        await withdrawParticipantFromEvent(participantIds, moveTo, event.eventId);
        onResetRowSelection();
      }
    },
    [event.eventId, isTeam, onResetRowSelection, showWithdrawDecisionDialog, withdrawParticipantFromEvent],
  );

  return (
    !!selectedFlatRows?.length && (
      <Grid
        container
        item
        alignItems="center"
        className={classes.participantTableActionContainer}
        justifyContent={'space-between'}
        spacing={2}
      >
        <Grid container item xs={12}>
          <ToolsGrid item>
            <ToolText>{selectedFlatRows?.length} Selected:</ToolText>
            <>
              {!isMainDrawGenerated && (
                <ToolButton size="large" startIcon={<AddIcon />} onClick={handleMoveToSelected}>
                  {isTeamTable ? t('moveToSelectedTeams') : t('moveToSelected')}
                </ToolButton>
              )}
              <ToolButton
                size="large"
                startIcon={<MoveIcon />}
                onClick={() => handleWithdrawal(EntryStatusEnum.ALTERNATE)}
              >
                {isTeamTable ? t('moveToAlternateTeams') : t('moveToAlternates')}
              </ToolButton>
              <ToolButton
                size="large"
                startIcon={<EditIcon />}
                onClick={() => {
                  onClickSetPlayerStatus(
                    selected.current?.map((p) => p.participantId),
                    onResetRowSelection,
                  );
                }}
              >
                {t('changeEntryStatus')}
              </ToolButton>
              {!isDoubles && (
                <ToolButton
                  size="large"
                  startIcon={<WithdrawIcon />}
                  onClick={() => handleWithdrawal(EntryStatusEnum.WITHDRAWN)}
                >
                  {t('withdraw')}
                </ToolButton>
              )}
            </>
          </ToolsGrid>
        </Grid>
      </Grid>
    )
  );
};

export default QualifyingBulkActions;
